import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SettingsEventType } from '../models/enums/settingsEventType';

/**
 * This class is used for all settings.
 * Static variables belong into the environment variables.
 * This service offers an UpdateObservable, to be notified when data needs to be reloaded because settings changed
 */
@Injectable()
export class SettingsService {
  private updateSubject = new Subject<SettingsEventType>();

  private readonly KEY_LAST_SELECTED_PROJECT_KEY = 'projectKey';
  private readonly KEY_LAST_SELECTED_PROJECT_TYPE = 'projectType';
  private readonly KEY_LAST_SELECTED_SPRINT_ID = 'sprintId';
  private readonly KEY_MPM_PROJECTS = 'mpmProjects';
  private readonly KEY_MPM_SPRINT_FUTURE = 'mpmSprintF';
  private readonly KEY_MPM_SPRINT_PAST = 'mpmSprintP';
  private readonly KEY_QUESTIONNAIRE_NAME = 'questionnaireName';
  private readonly KEY_MPC_PROGRAM_ID = 'programId';
  private readonly KEY_MPC_PROGRAM_INCREMENT_ID = 'programIncrementId';
  private readonly KEY_LANGUAGE = 'language';
  private readonly KEY_MARK_AS_UNSEEN_TIMEOUT = 'markAsUnseenTimeout';

  constructor() {
  }

  /**
  * components can subscribe to this observable to be notified when settings change and they need to reload data
  * @returns {Observable<void>}
  */
  getUpdateObservable(): Observable<SettingsEventType> {
    return this.updateSubject.asObservable();
  }

  /**
   * This function will inform all listeners to the update observable that a SettingsEvent has occured.
   *
   * @param eventType SettingsEventType to specify what Settings has been updated
   * @param callerName a string to identify who called this function, for debugging purposes
   */
  emitSettingsUpdate(eventType: SettingsEventType, callerName: string): void {
    console.log('emitSettingsUpdate called by ' + callerName + ' with type: ', SettingsEventType[eventType]);
    this.updateSubject.next(eventType);
  }

  getProjectName(): string {
    return localStorage.getItem(this.KEY_LAST_SELECTED_PROJECT_KEY);
  }

  getProjectType(): string {
    return localStorage.getItem(this.KEY_LAST_SELECTED_PROJECT_TYPE);
  }

  getQuestionnaireName(): string {
    return localStorage.getItem(this.KEY_QUESTIONNAIRE_NAME);
  }

  getSprintId(projectKey: string): number {
    return Number(localStorage.getItem(this.KEY_LAST_SELECTED_SPRINT_ID + ';' + projectKey));
  }

  getMpmProjects(): string {
    return localStorage.getItem(this.KEY_MPM_PROJECTS);
  }

  getMpmFutureSprint(): number {
    return Number(localStorage.getItem(this.KEY_MPM_SPRINT_FUTURE));
  }

  getMpmPastSprint(): number {
    return Number(localStorage.getItem(this.KEY_MPM_SPRINT_PAST));
  }

  getProgramIncrementId(programId: number): number {
    const id: string = localStorage.getItem(this.KEY_MPC_PROGRAM_INCREMENT_ID + ';' + programId);
    return (id) ? Number(id) : null;
  }

  getProgramId(): number {
    const id: string = localStorage.getItem(this.KEY_MPC_PROGRAM_ID);
    return (id) ? Number(id) : null;
  }

  getLanguage(): string {
    return localStorage.getItem(this.KEY_LANGUAGE);
  }

  getMarkAsUnseenTimeout(): number {
    const timeout = localStorage.getItem(this.KEY_MARK_AS_UNSEEN_TIMEOUT);
    return timeout ? Number(timeout) : 0;
  }

  setProjectKey(projectKey: string): void {
    localStorage.setItem(this.KEY_LAST_SELECTED_PROJECT_KEY, projectKey);
  }

  setProjectType(projectType: string): void {
    localStorage.setItem(this.KEY_LAST_SELECTED_PROJECT_TYPE, projectType);
  }

  setQuestionnaireName(questionnaireName: string): void {
    localStorage.setItem(this.KEY_QUESTIONNAIRE_NAME, questionnaireName);
  }

  /**
   * Store the sprintId of the last selected sprint of a specific project in localstorage
   */
  setSprintId(projectKey: string, sprintId: number): void {
    localStorage.setItem(this.KEY_LAST_SELECTED_SPRINT_ID + ';' + projectKey, sprintId.toString());
  }

  setMpmProjects(mpmProjects: string): void {
    localStorage.setItem(this.KEY_MPM_PROJECTS, mpmProjects);
  }

  setMpmFutureSprint(mpmFutureSprint: number): void {
    localStorage.setItem(this.KEY_MPM_SPRINT_FUTURE, mpmFutureSprint.toString());
  }

  setMpmPastSprint(mpmPastSprint: number): void {
    localStorage.setItem(this.KEY_MPM_SPRINT_PAST, mpmPastSprint.toString());
  }

  setProgramIncrementId(programId: number, programIncrementId: number): void {
    localStorage.setItem(this.KEY_MPC_PROGRAM_INCREMENT_ID + ';' + programId, programIncrementId.toString());
  }

  setProgramId(programId: number): void {
    localStorage.setItem(this.KEY_MPC_PROGRAM_ID, programId.toString());
  }

  setLanguage(language: string) {
    localStorage.setItem(this.KEY_LANGUAGE, language);
  }

  setMarkAsUnseenTimeout(timeout: number): void {
    localStorage.setItem(this.KEY_MARK_AS_UNSEEN_TIMEOUT, timeout.toString());
  }
}
